/**
 * @file WmsTransferEditPage.tsx
 * @description Карточка документа «Перемещение» (phase19.23 этап B; единый
 * шаблон документа — phase19.24 этап 1, шаг 1).
 *
 * Состав экрана — общий каркас складского документа (`DocumentLayout`): шапка
 * (крошка, номер, статус, подстрока, плитки meta) → полосы → позиции слева и
 * панель «Добавить строку» справа → движения по документу у проведённого.
 *
 * Шапка и действия живут в `TransferHeader`, запросы — в
 * `useTransferDocument`: странице остаётся раскладка.
 *
 * phase19.31 (зона «Склад», минимум по SC50): шапка — в верхней панели;
 * своя липкая полоса «Провести перемещение» убрана — на телефоне действие в
 * общей нижней полосе каркаса.
 */

import { Loader2 } from "lucide-react";
import { useParams } from "react-router";

import { useHasPermission } from "../../../auth/hooks";
import { Alert } from "../../../components/ui/alert";
import { DocumentLayout } from "../../../components/ui/DocumentLayout";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { SectionLabel } from "../../../components/ui/SectionLabel";
import { WmsDocumentMovements } from "../../../components/wms/WmsDocumentMovements";
import { TransferHeader } from "../../../components/wms/transfers/TransferHeader";
import { TransferItemForm } from "../../../components/wms/transfers/TransferItemForm";
import { TransferItemsList } from "../../../components/wms/transfers/TransferItemsList";
import { TransferNotices } from "../../../components/wms/transfers/TransferNotices";
import { TransferLotMovedDialog } from "../../../components/wms/transfers/TransferLotMovedDialog";
import { useTransferDocument } from "../../../components/wms/transfers/useTransferDocument";
import { useTransferStockOptions } from "../../../components/wms/transfers/useTransferStockOptions";
import {
  documentCommitHint,
  NOTHING_TO_COMMIT_HINT,
  placementNothingToPlaceText,
  unplaceableItemsText,
} from "../../../components/wms/document-texts";
import { useDocumentRowHighlight } from "../../../components/wms/useDocumentRowHighlight";
import {
  hasInterWarehouseRow,
  missingTargetRowNumbers,
} from "../../../lib/transfer-rows";

export function WmsTransferEditPage() {
  const { id, slug } = useParams<{ id: string; slug: string }>();
  // Межсклад проводит только администратор склада (OQ-46) — от права зависит
  // вариант «Другой склад» в форме строки.
  const canManage = useHasPermission("SHOP_WMS_MANAGE");
  const doc = useTransferDocument(id);
  const transfer = doc.transfer;
  const options = useTransferStockOptions(transfer?.fromWarehouseId ?? "");
  // «Подбор» → фасовка уже в документе → прокрутка к её строке и подсветка.
  const highlight = useDocumentRowHighlight("transfer-item");

  if (doc.loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="size-5 animate-spin text-primary" />
      </div>
    );
  }

  if (doc.error || !transfer) {
    return <Alert severity="error">{doc.error ?? "Перемещение не найдено"}</Alert>;
  }

  const isDraft = transfer.status === "DRAFT";
  const items = transfer.items ?? [];
  const from = transfer.fromWarehouseId;
  const blockedRows = missingTargetRowNumbers(items, from);
  // Лист размещения из приёмки: позиции без партии сервер в документ не берёт
  // — иначе кладовщик ищет в списке товар, которого там нет (решение
  // пользователя 2026-09-20). Текст один на полосу и на пустую таблицу.
  const unplaceable = transfer.unplaceableItems ?? [];
  const unplaceableText =
    unplaceable.length > 0 ? unplaceableItemsText(unplaceable) : null;
  // Другой случай пустого листа: ячейку указали прямо в приёмке, все партии
  // уже с адресом — документ открылся без строк, и это нормальный исход, а не
  // «добавьте первую строку» (находка QA 2026-09-20).
  const nothingToPlace =
    transfer.source === "PLACEMENT" &&
    !!transfer.refReceiptId &&
    items.length === 0 &&
    unplaceable.length === 0;
  const nothingToPlaceText = nothingToPlace
    ? placementNothingToPlaceText(transfer.receipt?.receiptNumber ?? null)
    : null;
  // Причину блокировки считаем один раз на странице: тем же текстом её пишут
  // строка под шапкой (широкий экран) и подпись над нижней полосой (телефон).
  const commitHint = nothingToPlace
    ? NOTHING_TO_COMMIT_HINT
    : documentCommitHint("transfer", items.length, blockedRows);
  const usedLotIds = items
    .map((item) => item.lotId)
    .filter((lotId): lotId is string => lotId !== null);
  // Фасовка «уже добавлена», когда в документе стоят ВСЕ её партии: свободная
  // партия — повод добавить ещё одну строку, а не повод гасить строку списка.
  const fullyUsedSkuIds = options.skus
    .filter(
      (sku) =>
        sku.lots.length > 0 &&
        sku.lots.every((lot) => usedLotIds.includes(lot.lotId)),
    )
    .map((sku) => sku.skuId);

  return (
    <DocumentLayout
      header={
        <TransferHeader
          transfer={transfer}
          backTo={`/shop/${slug}/wms/transfers`}
          commitHint={commitHint}
          hasInterWarehouse={hasInterWarehouseRow(items, from)}
          busy={doc.busy}
          committing={doc.committing}
          onCommit={() => void doc.commit()}
          onCancel={() => void doc.cancel()}
        />
      }
      notices={
        <TransferNotices
          notes={transfer.notes}
          isDraft={isDraft}
          // Пустой список сам объясняет причину (emptyText) — без повтора полосой (QA-5 D11).
          unplaceableText={items.length > 0 ? unplaceableText : null}
          optionsError={options.error}
        />
      }
      main={
        <section className="flex min-w-0 flex-col gap-2">
          <SectionLabel as="h2" count={items.length}>
            Позиции
          </SectionLabel>
          <TransferItemsList
            items={items}
            // Пустой лист размещения объясняет причину, а не зовёт «добавить
            // партию через панель справа»: добавлять нечего.
            emptyText={unplaceableText ?? nothingToPlaceText}
            highlightedId={highlight.highlightedId}
            rowDomId={highlight.rowDomId}
            fromWarehouseId={from}
            bins={options.bins}
            editable={isDraft}
            busy={doc.busy || doc.committing}
            onUpdate={(itemId, payload) => void doc.updateItem(itemId, payload)}
            onRemove={(itemId) => void doc.removeItem(itemId)}
          />
        </section>
      }
      aside={
        isDraft ? (
          <TransferItemForm
            fromWarehouseId={from}
            fromWarehouseName={
              transfer.fromWarehouse?.name ?? transfer.fromWarehouseName ?? "—"
            }
            skus={options.skus}
            bins={options.bins}
            targetWarehouses={options.targetWarehouses}
            usedLotIds={usedLotIds}
            fullyUsedSkuIds={fullyUsedSkuIds}
            onGoToExisting={(skuId) => {
              const row = items.find((item) => item.skuId === skuId);
              if (row) highlight.goTo(row.id);
            }}
            canMoveBetweenWarehouses={canManage}
            submitting={doc.busy}
            onSubmit={async (payload) => {
              const ok = await doc.addItem(payload);
              // Остаток мог измениться — перечитываем справочник формы.
              if (ok) options.reload();
              return ok;
            }}
          />
        ) : undefined
      }
      footer={
        transfer.status === "COMMITTED" ? (
          <div className="flex flex-col gap-2">
            <WmsDocumentMovements documentId={transfer.id} documentKind="transfer" />
            <NoticeStrip tone="info">
              Смена ячейки внутри склада остаток не меняет — движений по таким
              строкам нет.
            </NoticeStrip>
          </div>
        ) : null
      }
      dialogs={
        <TransferLotMovedDialog
          items={doc.movedLots}
          busy={doc.busy}
          onRefresh={() => void doc.refreshRows()}
          onClose={doc.dismissMovedLots}
        />
      }
    />
  );
}
